import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { fetchSettlementHistory, fetchSettlementWeeks } from "@admin/services/adminApi";
import { FilterBar, Pagination } from "@admin/components/Primitives";
import { paise } from "@admin/components/analytics/AnalyticsPrimitives";
import { STATUS_LABEL, STATUS_CLS, fmtIST } from "@admin/components/payouts/settlementFormat";

const STATUS_OPTIONS = [
  ["", "All statuses"], ["approved", "Pending payment"], ["paid", "Paid"], ["failed", "Failed"],
  ["requested,under_review,processing", "Processing"], ["rejected,cancelled,reversed", "Rejected / Cancelled / Reversed"],
];
const LIMIT = 25;
const inputCls = "h-9 rounded-md border border-slate-200 px-3 text-sm bg-white focus:outline-none focus:border-[#0A2540]";

function SortHeader({ label, field, sort, order, onSort, testid }) {
  const active = sort === field;
  const Icon = !active ? ArrowUpDown : order === "asc" ? ArrowUp : ArrowDown;
  return (
    <button type="button" onClick={() => onSort(field)} className={`inline-flex items-center gap-1 uppercase tracking-wider text-xs font-semibold ${active ? "text-[#0A2540]" : "text-slate-500"}`} data-testid={testid} aria-sort={active ? (order === "asc" ? "ascending" : "descending") : "none"}>
      {label}<Icon size={12} />
    </button>
  );
}

export function SettlementHistoryView({ onOpen }) {
  const [data, setData] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [status, setStatus] = useState("");
  const [week, setWeek] = useState("");
  const [lawyerQ, setLawyerQ] = useState("");
  const [refQ, setRefQ] = useState("");
  const [applied, setApplied] = useState({ lawyer_q: "", q: "" });
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchSettlementWeeks().then((w) => setWeeks(w.weeks || [])).catch(() => setWeeks([])); }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: LIMIT, sort, order };
    if (status) params.status = status;
    if (week) params.week_start = week;
    if (applied.lawyer_q) params.lawyer_q = applied.lawyer_q;
    if (applied.q) params.q = applied.q;
    fetchSettlementHistory(params).then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load settlement history"))
      .finally(() => setLoading(false));
  }, [page, sort, order, status, week, applied]);
  useEffect(() => { load(); }, [load]);

  const onSort = (field) => {
    setPage(1);
    if (sort === field) setOrder((o) => (o === "asc" ? "desc" : "asc"));
    else { setSort(field); setOrder("desc"); }
  };
  const applySearch = (e) => { e?.preventDefault(); setPage(1); setApplied({ lawyer_q: lawyerQ.trim(), q: refQ.trim() }); };
  const reset = () => { setStatus(""); setWeek(""); setLawyerQ(""); setRefQ(""); setApplied({ lawyer_q: "", q: "" }); setPage(1); setSort("created_at"); setOrder("desc"); };

  return (
    <div data-testid="settlement-history-view">
      <form onSubmit={applySearch}>
        <FilterBar testid="history-filters">
          <select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} className={inputCls} data-testid="history-filter-status" aria-label="Status">
            {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={week} onChange={(e) => { setPage(1); setWeek(e.target.value); }} className={inputCls} data-testid="history-filter-week" aria-label="Settlement week">
            <option value="">All settlement weeks</option>
            {weeks.map((w) => <option key={w.start} value={w.start}>{w.label} ({w.count})</option>)}
          </select>
          <input value={lawyerQ} onChange={(e) => setLawyerQ(e.target.value)} placeholder="Lawyer name / email / id" className={`${inputCls} min-w-[200px]`} data-testid="history-filter-lawyer" />
          <input value={refQ} onChange={(e) => setRefQ(e.target.value)} placeholder="UTR / settlement reference" className={`${inputCls} min-w-[200px]`} data-testid="history-filter-ref" />
          <button type="submit" className="h-9 px-4 rounded-md bg-[#0A2540] text-white text-xs font-bold hover:bg-[#123560]" data-testid="history-filter-apply">Search</button>
          <button type="button" onClick={reset} className="h-9 px-3 rounded-md border border-slate-200 text-xs font-bold text-slate-600 hover:border-[#0A2540]" data-testid="history-filter-reset">Reset</button>
        </FilterBar>
      </form>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto" data-testid="history-table">
        {!data ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="history-loading">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="history-empty">No settlements match these filters.</div>
        ) : (
          <table className={`w-full text-sm ${loading ? "opacity-60" : ""}`}>
            <thead className="bg-slate-50 text-slate-500">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold">Settlement</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold">Lawyer</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold">Period</th>
                <th className="text-left px-4 py-3"><SortHeader label="Amount" field="amount" sort={sort} order={order} onSort={onSort} testid="history-sort-amount" /></th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold">Status</th>
                <th className="text-left px-4 py-3"><SortHeader label="Created" field="created_at" sort={sort} order={order} onSort={onSort} testid="history-sort-created" /></th>
                <th className="text-left px-4 py-3"><SortHeader label="Paid" field="paid_at" sort={sort} order={order} onSort={onSort} testid="history-sort-paid" /></th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold">UTR / Ref</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-semibold">Failure reason</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.payout_id} onClick={() => onOpen(p.payout_id)} className="border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors" data-testid={`history-row-${p.payout_id}`}>
                  <td className="px-4 py-3 font-bold text-[#0A2540] whitespace-nowrap" data-testid={`history-ref-${p.payout_id}`}>{p.payout_reference || p.payout_id}</td>
                  <td className="px-4 py-3" data-testid={`history-lawyer-${p.payout_id}`}>{p.lawyer_name}{!p.lawyer_resolved && <span className="ml-1 text-[10px] text-slate-400">(unresolved)</span>}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`history-period-${p.payout_id}`}>{p.settlement_period?.label || "—"}</td>
                  <td className="px-4 py-3 font-extrabold text-[#0A2540] whitespace-nowrap" data-testid={`history-amount-${p.payout_id}`}>{paise(p.amount_paise)}</td>
                  <td className="px-4 py-3"><span className={`text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${STATUS_CLS[p.status] || STATUS_CLS.processing}`} data-testid={`history-status-${p.payout_id}`}>{STATUS_LABEL[p.status] || p.status}</span></td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`history-created-${p.payout_id}`}>{fmtIST(p.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap" data-testid={`history-paid-${p.payout_id}`}>{fmtIST(p.paid_at)}</td>
                  <td className="px-4 py-3 text-xs font-mono" data-testid={`history-utr-${p.payout_id}`}>{p.provider_utr || p.provider_ref || "—"}</td>
                  <td className="px-4 py-3 text-xs text-rose-700 max-w-[220px] truncate" title={p.failure_reason || ""} data-testid={`history-failure-${p.payout_id}`}>{p.failure_reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination total={data.total} skip={(data.page - 1) * data.limit} limit={data.limit} onChange={(skip) => setPage(Math.floor(skip / LIMIT) + 1)} testid="history-pagination" />}
      <p className="mt-3 text-[11px] text-slate-400" data-testid="history-note">Read-only audit view. Amounts, periods and references are the values stored when each settlement was created or settled — they are never recalculated. Failed settlements stay here permanently, even after their earnings are re-settled.</p>
    </div>
  );
}
